import { HOSTILE_MAX_STANDING, standingTier, type FactionName, type FactionStanding } from "./factionStanding";
import type { NpcFaction } from "./types";

/**
 * Station guard fleets and the docking gate (design doc 16.94, Don's
 * direction): 5-10 ships at every station, faction or pirate, and docking
 * allowed only when they are not shooting at you.
 *
 * Pure and separate from the Expedition View so the rules that decide who gets
 * shot at can be tested without a canvas or a render loop -- the same reason
 * zoomScale.ts and stationMarket.ts live outside it. The view supplies
 * positions and frames; this file supplies the answers.
 */

export type GalaxyRegion = "safe" | "neutral" | "crimson" | "azure" | "solthane" | "pirate";

/**
 * Who owns the guns at a station in this region.
 *
 * `safe` and `neutral` return null: those are the shared core, policed by
 * nobody in particular, and there is no faction whose opinion of the captain
 * could make its guards open fire. Their stations still have guards for
 * presence (see stationGuardCount) -- they simply have no grievance to act on.
 */
export function stationGuardFaction(region: GalaxyRegion): FactionName | null {
  switch (region) {
    case "crimson":
      return "Crimson Concord";
    case "azure":
      return "Azure Compact";
    case "solthane":
      return "Solthane Accord";
    case "pirate":
      return "Pirate";
    default:
      return null;
  }
}

/**
 * Which NPC roster the guard ships come from, for silhouettes and unit stats.
 *
 * The NPC roster only distinguishes Pirate / Faction / Alien -- the three
 * political factions share one "Faction" ship line -- so this collapses to two
 * cases. Deliberately kept as its own function rather than inlined at the
 * spawn site: `stationGuardFaction` answers "whose opinion decides if they
 * shoot" and this answers "what do they fly", and those are different
 * questions that will diverge the moment per-faction hulls exist.
 */
export function guardNpcFaction(region: GalaxyRegion): NpcFaction {
  return region === "pirate" ? "Pirate" : "Faction";
}

export const MIN_STATION_GUARDS = 5;
export const MAX_STATION_GUARDS = 10;

/**
 * How many guards this station keeps, in Don's 5-10 band.
 *
 * Hubs sit at the top of the band and outposts at the bottom, which is both
 * intuitive and useful: forcing your way into a frontier outpost is a real
 * option for a strong captain, and forcing your way into a hub is not.
 * Derived from the station id so it is stable forever without storing a number
 * per station -- the same reasoning as the derived market.
 */
export function stationGuardCount(stationId: string, stationClass: "hub" | "frontier"): number {
  let hash = 0x811c9dc5;
  const key = `guards ${stationId}`;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  // hub 8-10, frontier 5-7 -- both inside the 5-10 band Don specified.
  const [lo, hi] = stationClass === "hub" ? [8, 10] : [5, 7];
  return lo + (hash % (hi - lo + 1));
}

export type Disposition = "hostile" | "tolerated" | "welcome";

/**
 * How a station's guards regard this captain -- the three bands Don asked for.
 *
 * - **hostile**: fired on approach. Dock only by destroying every guard.
 * - **tolerated**: not fired on. Docks and trades, but is not a friend.
 * - **welcome**: not fired on, and treated as one of their own.
 *
 * A station with no owning faction is always `tolerated`: nobody there has an
 * opinion to hold. Note this makes the *uniform* reading of Don's rule --
 * "pirate stations are the same as faction stations except for where they are"
 * -- which means a captain at Neutral standing can dock in pirate space. If
 * pirate space should instead turn away anyone not actually aligned, that is a
 * single change here (require "welcome"), and 16.94 records the tradeoff.
 */
export function guardDisposition(standing: FactionStanding, region: GalaxyRegion): Disposition {
  const owner = stationGuardFaction(region);
  if (!owner) return "tolerated";
  const tier = standingTier(standing[owner]);
  if (tier === "Hostile") return "hostile";
  if (tier === "Friendly") return "welcome";
  return "tolerated";
}

/**
 * Whether guards open fire without being provoked.
 *
 * Kept separate from the disposition itself because the view needs the boolean
 * every frame and the three-way label only for display -- and because "shoots
 * at me" is the one property that must never drift from the band that decides
 * it.
 */
export function guardsAttackOnSight(standing: FactionStanding, region: GalaxyRegion): boolean {
  return guardDisposition(standing, region) === "hostile";
}

export interface DockCheck {
  allowed: boolean;
  /** Player-facing reason when refused. Empty when allowed. */
  reason: string;
}

/**
 * The docking gate: allowed unless guards are actively engaging.
 *
 * Exactly Don's rule -- "only dock if they are not being attacked currently,
 * meaning they have to either kill all the ships or have high enough standing
 * that they are not attacked on-sight". Both routes fall out of one condition
 * rather than being special-cased, because a welcome captain has no engaged
 * guards and a captain who cleared the field has none left.
 *
 * Deliberately keyed on guards actually ENGAGED rather than on standing: a
 * captain who fights their way in has earned the dock even though their
 * standing never moved, and a captain who is welcome but shot first has not.
 */
export function checkDocking(engagedGuards: number, disposition: Disposition): DockCheck {
  if (engagedGuards > 0) {
    return {
      allowed: false,
      reason:
        disposition === "hostile"
          ? `Station guns are live — ${engagedGuards} guard${engagedGuards === 1 ? "" : "s"} still engaging. Destroy them or withdraw.`
          : `Cannot dock while under fire — ${engagedGuards} guard${engagedGuards === 1 ? "" : "s"} still engaging.`
    };
  }
  return { allowed: true, reason: "" };
}

/**
 * How far below the Hostile threshold a captain is, for the approach warning.
 *
 * Exists so the Space Map and the approach banner can tell a captain they are
 * about to be shot at BEFORE it happens. Hostility that arrives without notice
 * is not a decision the captain made, it is one that happened to them.
 */
export function standingUntilHostile(standing: FactionStanding, region: GalaxyRegion): number | null {
  const owner = stationGuardFaction(region);
  if (!owner) return null;
  return standing[owner] - HOSTILE_MAX_STANDING;
}
